import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import PropertiesPage from "@/pages/PropertiesPage";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import BudgetConfigPage from "@/pages/BudgetConfigPage";
import ReportsPage from "@/pages/ReportsPage";
import ActualsPage from "@/pages/ActualsPage";
import LandingPage from "@/pages/LandingPage";
import PricingPage from "@/pages/PricingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import SettingsPage from "@/pages/SettingsPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import BudgetComparisonPage from "@/pages/BudgetComparisonPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies with every request
axios.defaults.withCredentials = true;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider Component
// Uses HTTPOnly cookies for secure token storage (set by backend)
// Also supports legacy Bearer token for backward compatibility during transition
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token")); // Keep for backward compat
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // CRITICAL: If returning from OAuth callback, skip the /me check.
      // AuthCallback will exchange the session_id and establish the session first.
      if (window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }
      
      // Try to get user info - cookies are sent automatically
      // Also send Bearer token as fallback for backward compatibility
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/auth/me`, { headers });
        setUser(response.data);
      } catch (error) {
        // Auth failed - clear localStorage token if it exists
        if (token) {
          localStorage.removeItem("token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData, password_change_required } = response.data;
    // Store token in localStorage for backward compatibility (will be phased out)
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser({ ...userData, password_change_required });
    return { ...userData, password_change_required };
  };

  const register = async (email, password, name, inviteCode = null) => {
    const payload = { email, password, name };
    if (inviteCode) {
      payload.invite_code = inviteCode;
    }
    const response = await axios.post(`${API}/auth/register`, payload);
    const { access_token, user: userData } = response.data;
    // Store token in localStorage for backward compatibility
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear HTTPOnly cookie
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    // Clear localStorage token
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  // Returns headers with token for backward compatibility
  // Cookies are sent automatically with withCredentials=true
  const getAuthHeader = () => {
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, getAuthHeader }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full spinner"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/welcome" replace />;
  }

  return children;
};

// Admin Route Component - Only allows admin user
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const isAdmin = user?.email === "anandsangwan@gmail.com";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full spinner"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router Component - Handles OAuth callback detection
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Detect session_id during render (NOT in useEffect)
  // This synchronous check prevents race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallbackPage />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/welcome" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      
      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/properties"
        element={
          <ProtectedRoute>
            <PropertiesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/properties/:id"
        element={
          <ProtectedRoute>
            <PropertyDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/budget/:propertyId"
        element={
          <ProtectedRoute>
            <BudgetConfigPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/budget/:propertyId/edit/:budgetId"
        element={
          <ProtectedRoute>
            <BudgetConfigPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/:budgetId"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compare/:budgetId"
        element={
          <ProtectedRoute>
            <BudgetComparisonPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/actuals/:budgetId"
        element={
          <ProtectedRoute>
            <ActualsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminDashboardPage />
          </AdminRoute>
        }
      />
      <Route path="*" element={<Navigate to="/welcome" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
